import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaCheck, FaEye, FaPen, FaTrash } from "react-icons/fa";
import { TiCreditCard } from "react-icons/ti";
import { MdLocalShipping, MdOutlineRestorePage } from "react-icons/md";
import Badge from "UpdatedFeatures/Badge";
import { toast } from "react-toastify";
import { Puff } from "react-loader-spinner";
import Pagination from "UpdatedComponents/Pagination";
import {
    deleteSubscription,
    getSubscriptions,
    //   createSubscription,
    restoreSubscription
} from "UpdatedFeatures/api";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import RestoreModal from "UpdatedFeatures/RestoreModal";
import TrashedSwitch from "UpdatedComponents/TrashedSwitch";
import EditSubscriptionModal from "./EditSubscriptionModal";
import DeleteSubscriptionModal from "./DeleteSubscriptionModal";

const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("fr-FR");
};

// Define status constants
const STATUS = {
    Active: 1,
    Desactivated: 2,
    All: null,
};
const VisibilityStatus = {
    All: 0,
    Visible: 1,
    Hidden: 2,
};


const SubscriptionsManagementTable = ({ currentTab }) => {
    const [search, setSearch] = useState("");
    const [isArchived, setIsArchived] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const queryClient = useQueryClient();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [restoreModalOpen, setRestoreModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState({ id: STATUS.All, label: "Tous" });
    console.log(selectedStatus)
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)

    const deleteMutation = useMutation({
        mutationFn: deleteSubscription,
        onSuccess: () => {
            queryClient.invalidateQueries(['subscriptions']);
            toast.success("Abonnement supprimé avec succès");
        },
        onError: (error) => {
            toast.error(error?.response?.data?.error?.[0] || "Erreur lors de la suppression");
        }
    });

    const [subscriptionToRestore, setSubscriptionToRestore] = useState(null);

    const restoreMutation = useMutation({
        mutationFn: restoreSubscription,
        onSuccess: () => {
            queryClient.invalidateQueries(['subscriptions']);
            toast.success("Abonnement restauré avec succès");
            setRestoreModalOpen(false);
        },
        onError: (error) => {
            toast.error(error.message || "Erreur lors de la restauration");
        }
    });

    const handleRestoreClick = (subscription) => {
        setSubscriptionToRestore(subscription);
        setRestoreModalOpen(true);
    };

    const handleRestoreConfirm = () => {
        if (subscriptionToRestore) {
            restoreMutation.mutate(subscriptionToRestore.id);
        }
    };

    const { data: subscriptionsData, isLoading, isError } = useQuery({
        queryKey: ["subscriptions", page, perPage, search, isArchived, currentTab, selectedStatus.id],
        queryFn: () => getSubscriptions(page, perPage, search, isArchived, currentTab, selectedStatus.id),
    });

    console.log(subscriptionsData)

    const handleEditSubscription = (sub) => {
        setSelectedSubscription(sub);
        setIsEditModalOpen(true);
    }
    const handleDeleteClick = (sub) => {
        setSelectedSubscription(sub);
        setDeleteModalOpen(true);
    }

    const handleDeleteConfirm = () => {
        if (selectedSubscription) {
            deleteMutation.mutate(selectedSubscription.id);
        }
        setDeleteModalOpen(false)
    };

    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="text-red-600 font-medium text-lg">
                    Une erreur s'est produite lors du chargement des données
                </span>
                <button
                    onClick={() => queryClient.refetchQueries(['subscriptions'])}
                    className="px-4 py-2 bg-[#b269ae] text-white rounded-md hover:bg-[#9c58a3] transition-colors"
                >
                    Réessayer
                </button>
            </div>
        );
    }


    return (
        <div className="px-4 sm:px-6 lg:px-8 mt-12">
            <div className="sm:flex sm:items-center justify-between space-y-3">
                <div className="relative w-1/3">
                    <form onSubmit={(e) => e.preventDefault()} className="flex items-center">
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full p-3 pl-20 text-sm border border-[#b269ae] rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-10">
                            <div className="bg-gray-300 rounded-md px-1">
                                <span className="text-xs text-white">SUB</span>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex items-center gap-2">
                        <select
                            value={selectedStatus.id}
                            onChange={(e) => {
                                const selected = Object.entries(STATUS).find(([_, v]) => v === Number(e.target.value) || v === null);
                                setSelectedStatus({ id: selected[1], label: selected[0] });
                            }}
                            className="border border-[#b269ae] rounded-md p-2"
                        >
                            {Object.entries(VisibilityStatus).map(([label, value]) => (
                                <option key={value} value={value}>
                                    {label === 'All' ? 'Tous' : label === 'Hidden' ? 'Non Visible' : 'Visible'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <TrashedSwitch enabled={isArchived} setEnabled={setIsArchived} />
                    </div>

                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center justify-center px-3 py-2 rounded-full bg-[#b269ae] text-white font-semibold transition-all duration-300 hover:bg-yellow-400"
                    >
                        <span className="text-sm">+ Créer un abonnement</span>
                    </button>
                </div>
            </div>
            {isLoading ?
                <div className="flex items-center justify-center min-h-[400px]">
                    <Puff visible={true} height="80" width="80" color="#895a7c" ariaLabel="puff-loading" />
                </div> : (
                    <div className="mt-8 flow-root">
                        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-[#b269ae]">
                                            <tr>
                                                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-normal text-gray-100 sm:pl-6">Référence</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">Label</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">Description</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">Options</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">Duration</th>
                                                <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">Visibilité</th>
                                                <th className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {!subscriptionsData?.data?.length ? (
                                                <tr>
                                                    <td colSpan="8" className="py-8 text-center text-gray-500">
                                                        Aucun abonnement trouvé
                                                    </td>
                                                </tr>
                                            ) : (
                                                subscriptionsData.data.map((item) => (
                                                    <tr key={item.id}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            SUB{item.id}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {item.label}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate max-w-2xl">
                                                            {item.description}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 flex items-center gap-2">
                                                            {item.shipping_mode_ids.length > 0 && (
                                                                <span className="flex gap-2 items-center"> <FaCheck className="w-2 h-2 text-primary" />Mode d'Expedition</span>
                                                            )}
                                                            {item.shipping_categ_ids.length > 0 && (
                                                                <span className="flex gap-2 items-center"><FaCheck className="w-2 h-2 text-primary" />Categorie d'Expedition</span>
                                                            )}
                                                        </td>

                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {item.duration_value}{" "}
                                                            {item.duration_unit === "month"
                                                                ? "Mois"
                                                                : item.duration_unit === "week"
                                                                    ? "Semaines"
                                                                    : "Année"}
                                                        </td>

                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            <Badge status={item.visible ? 2 : 3} type="Subscriptions" />
                                                        </td>
                                                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                            {isArchived ? (
                                                                <button
                                                                    className="text-[#b269ae] hover:text-purple-300"
                                                                    onClick={() => handleRestoreClick(item)}
                                                                >
                                                                    <MdOutlineRestorePage size={25} />
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="text-[#b269ae] hover:text-purple-300"
                                                                        onClick={() => handleEditSubscription(item)}
                                                                    >
                                                                        <FaPen />
                                                                    </button>
                                                                    <button
                                                                        className="text-[#b269ae] hover:text-purple-300 ml-4"
                                                                        onClick={() => { handleDeleteClick(item) }}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            <Pagination
                prevPage={() => setPage(p => Math.max(p - 1, 1))}
                nextPage={() => setPage(p => p + 1)}
                totalPages={Math.ceil(subscriptionsData?.total / perPage)}
                setPage={setPage}
            />
            <CreateSubscriptionModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
            <EditSubscriptionModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} subscription={selectedSubscription} />
            <DeleteSubscriptionModal
                open={deleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteModalOpen(false)}
                isLoading={deleteMutation.isPending}
            />
            <RestoreModal
                open={restoreModalOpen}
                onConfirm={handleRestoreConfirm}
                onCancel={() => setRestoreModalOpen(false)}
                isLoading={restoreMutation.isPending}
            />
        </div>
    );
}

export default SubscriptionsManagementTable;
